import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Paper, Grid, Box, Typography, Snackbar, Alert, useMediaQuery, useTheme
} from '@mui/material';
import { useAuthStore } from '../store/authStore';
import { useThemeContext } from '../components/contexts/ThemeContext';

// Modułowe komponenty ustawień
import UserManagement from './settings/UserManagement';
import ClientTariffs from './settings/ClientTariffs';
import AccountantTariffs from './settings/AccountantTariffs';
import ActivityHistory from './settings/ActivityHistory';
import SystemMaintenance from './settings/SystemMaintenance';
import SecuritySettings from './settings/SecuritySettings';

function Settings() {
    const { token, roles, fullName } = useAuthStore();
    const userName = fullName || 'Użytkownik';
    const canDeleteTariff = roles?.some(r => ["ADMIN", "MANAGER", "ADMIN_SUPERVISOR"].includes(r));
    const canManageAdminSettings = roles?.some(r => ["ADMIN", "MANAGER", "ADMIN_SUPERVISOR"].includes(r));

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { mode } = useThemeContext();
    const darkTheme = mode === 'dark';

    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');

    const getDefaultTab = () => {
        if (tabFromUrl) return tabFromUrl;
        return canManageAdminSettings ? 'users' : 'security';
    };

    const [activeTab, setActiveTab] = useState(getDefaultTab());
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (tabFromUrl && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl]);

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        setSearchParams({ tab: newTab });
    };

    const handleNotify = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const adminNavItems = [
        {
            id: 'users',
            label: 'Użytkownicy i Uprawnienia',
            subtitle: 'Konta, uprawnienia, role',
            icon: 'fas fa-user-shield',
            color: '#3B82F6',
            gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
        },
        {
            id: 'clientTariffs',
            label: 'Cenniki Klientów',
            subtitle: 'Pakiety, limity, stawki',
            icon: 'fas fa-file-invoice-dollar',
            color: '#10B981',
            gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
        },
        {
            id: 'accountantTariffs',
            label: 'Stawki Księgowych',
            subtitle: 'Wynagrodzenia, dokumenty',
            icon: 'fas fa-calculator',
            color: '#8B5CF6',
            gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)'
        },
        {
            id: 'activity',
            label: 'Historia Aktywności',
            subtitle: 'Dziennik audytu zdarzeń',
            icon: 'fas fa-history',
            color: '#F59E0B',
            gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
        },
        {
            id: 'system',
            label: 'Konserwacja Systemu',
            subtitle: 'Pamięć podręczna (Cache)',
            icon: 'fas fa-sliders-h',
            color: '#EF4444',
            gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
        }
    ];

    const securityNavItem = {
        id: 'security',
        label: 'Bezpieczeństwo i 2FA',
        subtitle: 'Weryfikacja dwuetapowa',
        icon: 'fas fa-shield-alt',
        color: '#10B981',
        gradient: 'linear-gradient(135deg, #10B981 0%, #047857 100%)'
    };

    const navItems = canManageAdminSettings
        ? [...adminNavItems, securityNavItem]
        : [securityNavItem];

    return (
        <Box sx={{ width: '100%', pb: 6, pt: 1 }}>
            {/* Globalny Snackbar powiadomień */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        fontWeight: 500
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Główny układ: Pasek boczny (Sidebar / Navigation Rail) + Obszar roboczy */}
            <Grid container spacing={3} alignItems="flex-start">
                {/* LEWA KOLUMNA: Nawigacja kategorii */}
                <Grid item xs={12} md={3.8} lg={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                            backgroundColor: darkTheme ? '#161B22' : '#FFFFFF',
                            boxShadow: darkTheme ? '0 4px 20px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.03)'
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                px: 1.5,
                                pt: 1,
                                pb: 1.5,
                                display: 'block',
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: darkTheme ? '#64748B' : '#94A3B8',
                                fontSize: '11px'
                            }}
                        >
                            Moduły konfiguracji
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: 1, overflowX: isMobile ? 'auto' : 'visible' }}>
                            {navItems.map((item) => {
                                const isActive = activeTab === item.id;
                                return (
                                    <Box
                                        key={item.id}
                                        onClick={() => handleTabChange(item.id)}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.8,
                                            transition: 'all 180ms ease-in-out',
                                            flexShrink: 0,
                                            minWidth: isMobile ? '200px' : 'auto',
                                            backgroundColor: isActive
                                                ? (darkTheme ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.07)')
                                                : 'transparent',
                                            border: '1px solid',
                                            borderColor: isActive
                                                ? (darkTheme ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.25)')
                                                : 'transparent',
                                            position: 'relative',
                                            '&:hover': {
                                                backgroundColor: isActive
                                                    ? (darkTheme ? 'rgba(59, 130, 246, 0.16)' : 'rgba(59, 130, 246, 0.10)')
                                                    : (darkTheme ? 'rgba(255, 255, 255, 0.04)' : '#F8FAFC'),
                                                transform: 'translateX(2px)'
                                            }
                                        }}
                                    >
                                        {/* Akcentowy wskaźnik po lewej stronie */}
                                        {isActive && !isMobile && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: -6,
                                                    width: 4,
                                                    height: '60%',
                                                    borderRadius: '4px',
                                                    backgroundColor: item.color
                                                }}
                                            />
                                        )}

                                        {/* Ikona w zaokrąglonym badge */}
                                        <Box
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: '9px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: isActive ? item.gradient : (darkTheme ? 'rgba(255,255,255,0.06)' : '#F1F5F9'),
                                                color: isActive ? '#FFFFFF' : (darkTheme ? '#94A3B8' : '#64748B'),
                                                fontSize: '15px',
                                                flexShrink: 0,
                                                boxShadow: isActive ? `0 3px 10px ${item.color}40` : 'none',
                                                transition: 'all 200ms ease'
                                            }}
                                        >
                                            <i className={item.icon}></i>
                                        </Box>

                                        {/* Etykieta i podtytuł */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: isActive ? 700 : 500,
                                                    color: isActive
                                                        ? (darkTheme ? '#F8FAFC' : '#0F172A')
                                                        : (darkTheme ? '#CBD5E1' : '#334155'),
                                                    fontSize: '13.5px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: darkTheme ? '#64748B' : '#94A3B8',
                                                    fontSize: '11.5px',
                                                    display: 'block',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {item.subtitle}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Grid>

                {/* PRAWA KOLUMNA: Aktywny panel zawartości */}
                <Grid item xs={12} md={8.2} lg={9}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, sm: 3.5 },
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                            backgroundColor: darkTheme ? '#161B22' : '#FFFFFF',
                            boxShadow: darkTheme ? '0 4px 20px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.03)',
                            minHeight: 520
                        }}
                    >
                        {activeTab === 'users' && canManageAdminSettings && (
                            <UserManagement
                                token={token}
                                userName={userName}
                                onNotify={handleNotify}
                                darkTheme={darkTheme}
                            />
                        )}

                        {activeTab === 'clientTariffs' && canManageAdminSettings && (
                            <ClientTariffs
                                token={token}
                                userName={userName}
                                onNotify={handleNotify}
                                canDeleteTariff={canDeleteTariff}
                                darkTheme={darkTheme}
                            />
                        )}

                        {activeTab === 'accountantTariffs' && canManageAdminSettings && (
                            <AccountantTariffs
                                token={token}
                                userName={userName}
                                onNotify={handleNotify}
                                darkTheme={darkTheme}
                            />
                        )}

                        {activeTab === 'activity' && canManageAdminSettings && (
                            <ActivityHistory
                                token={token}
                                darkTheme={darkTheme}
                            />
                        )}

                        {activeTab === 'system' && canManageAdminSettings && (
                            <SystemMaintenance
                                token={token}
                                userName={userName}
                                onNotify={handleNotify}
                                darkTheme={darkTheme}
                            />
                        )}

                        {activeTab === 'security' && (
                            <SecuritySettings
                                token={token}
                                onNotify={handleNotify}
                                darkTheme={darkTheme}
                            />
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Settings;
